import { NPS_ICON_MAPPING, NPS_CATEGORY } from './constants';

export type UserProfile = {
  firstName?: string;
  surName?: string;
  maskedDateOfBirth?: string;
  gender?: string;
  maskedEmail?: string;
  maskedPan?: string;
  panNumber?: string;
  flatNo?: string;
  city?: string;
  state?: string;
  pincode?: string;
};

export type UserSessionObjectType = {
  userConsentActive: boolean | null;
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  referenceId: string | null;
  accounts: any[] | null;
  templateType: string | null;
  pfmType: string | null;
  trackingId: string | null;
  isLoanJourneyEnabled: boolean;
  journeyType: string;
  tenant: string | null;
  sessionId: string | null;
  isNewUser: boolean;
  trendsVisible: boolean;
  existingUserJourneyStartedOnce: boolean;
  altJourney: string;
  altJourneyTheme: altJourneyTheme;
  sdkFlowSelection: boolean;
  mahindraTheme: string;
  isPanAvailable: boolean | undefined;
  jfsTheme: string | null;
  maskedMobileNumber?: string | null;
  userProfile?: UserProfile | null;
  isNtb: boolean;
  isBankConsentAvailable: boolean;
  channelType: string | null;
  isWhitelisted: boolean
};

export enum altJourneyTheme {
  YesGlory = 'YesGlory',
  YesGrandure = 'YesGrandure',
  YesPremia = 'YesPremia',
  YesFirst = 'YesFirst',
  YesPropsperity = 'YesPropsperity',
  YesPrivate = 'YesPrivate',
}

export enum altJourneyType {
  YESONLINE = 'YESONLINE',
}

export enum JfsTheme {
  Purple = 'purple',
  Gold = 'gold',
}

export const TenantType = {
  PB: process.env.REACT_APP_TENANT_ID,
  YESBANK: process.env.REACT_APP_TENANT_ID_YB,
  MAHINDRA: process.env.REACT_APP_TENANT_ID_MAHINDRA,
  JFS: process.env.REACT_APP_TENANT_ID_JFS,
  AU: process.env.REACT_APP_TENANT_ID_AU,
  PENSION_BAZAAR: process.env.REACT_APP_TENANT_ID_PENSION_BAZAAR,
  JAR: process.env.REACT_APP_TENANT_ID_JAR,
  BOB: process.env.REACT_APP_TENANT_ID_BOB,
  MUTHOOT: process.env.REACT_APP_TENANT_ID_MUTHOOT,
  STABLEMONEY: process.env.REACT_APP_TENANT_ID_STABLEMONEY,
  BOB_V2: process.env.REACT_APP_TENANT_ID_BOB_V2,
  SIB: process.env.REACT_APP_TENANT_ID_SIB,
};

export type UserDetailsType = {
  accessToken: string;
  refreshToken: string;
  userConsentActive: boolean;
  userId: string;
};
export type InitiateConsentResponseType = {
  redirectionUrl: string;
  referenceId: string;
};

export enum TransactionType {
  DEBIT,
  CREDIT,
}
export enum LOADERTYPE {
  GENERIC,
  DATALOADER,
}

export interface TransactionResponse {
  id: string;
  accountId: string;
  transactionId: string;
  uniqueId: string;
  txnRefId: string;
  reference: string;
  narration: string;
  counterParty: string;
  paymentMode: string;
  type: string;
  amount: number;
  balance: number;
  dateOfTransaction: string;
  category: string;
  subCategory: string;
  merchantName: string;
  displayName: string;
  categoryCode: string;
  merchantLogoCode: string;
  monthYear: string;
}

export interface PageResponse<T> {
  totalElements: number;
  totalPages: number;
  elements: T[];
}

export type TransactionsPageResponse = PageResponse<TransactionResponse>;

export enum AccountStatusType {
  ACTIVE = 'ACTIVE',
  REVOKE_INITIATED = 'REVOKE_INITIATED',
  RE_ANALYTICS_IN_PROGRESS = 'RE_ANALYTICS_IN_PROGRESS',
  INACTIVE = 'INACTIVE',
}

export enum FIIntegrationType {
  ETB = 'ETB',
  NON_ETB = 'NON_ETB',
  MANUAL_FD = 'MANUAL_FD',
  EPFO_DIGITAP = 'EPFO_DIGITAP',
  CREDIT_HEALTH_EXPERIAN = 'CREDIT_HEALTH_EXPERIAN',
}

export enum FIType {
  NPS = 'NPS',
  DEPOSIT = 'DEPOSIT',
  TERM_DEPOSIT = 'TERM_DEPOSIT',
  TERM_DEPOSIT_HYPHEN = 'TERM-DEPOSIT',
  RECURRING_DEPOSIT = 'RECURRING_DEPOSIT',
  SIP = 'SIP',
  CP = 'CP',
  GOVT_SECURITIES = 'GOVT_SECURITIES',
  EQUITIES = 'EQUITIES',
  BONDS = 'BONDS',
  DEBENTURES = 'DEBENTURES',
  MUTUAL_FUNDS = 'MUTUAL_FUNDS',
  ETF = 'ETF',
  IDR = 'IDR',
  CIS = 'CIS',
  AIF = 'AIF',
  INSURANCE_POLICIES = 'INSURANCE_POLICIES',
  INVIT = 'INVIT',
  REIT = 'REIT',
  GSTR1_3B = 'GSTR1_3B',
  GENERAL_INSURANCE = 'GENERAL_INSURANCE',
  GSTR = 'GSTR',
  LIFE_INSURANCE = 'LIFE_INSURANCE',
  EPFO = 'EPFO',
  CREDIT_BUREAU = 'CREDIT_BUREAU',
  OTHER = 'OTHER',
}

// Distinguishes Term vs Recurring within the "other deposit" bucket on YesBank cards.
export enum DepositSubType {
  TERM = 'TERM',
  RECURRING = 'RECURRING',
}

// Suffixes appended to fipId to keep groupByKey buckets separated per state/subtype.
// Used by YesBank Spends to render one card per (FIP, deposit subtype, state).
export enum DepositCardSuffix {
  TD_SUCCESS = '$tdSuccess',
  RD_SUCCESS = '$rdSuccess',
  TD_LOADING = '$tdLoading',
  RD_LOADING = '$rdLoading',
  TD_FAILED = '$tdFailed',
  RD_FAILED = '$rdFailed',
  TD_REVOKED = '$tdRevoked',
  RD_REVOKED = '$rdRevoked',
  TD_EXPIRED = '$tdExpired',
  RD_EXPIRED = '$rdExpired',
  BANK_LOADING = '$loading',
  BANK_FAILED = '$failed',
  BANK_REVOKED = '$revoked',
  BANK_EXPIRED = '$expired',
}

// FI Status enum (matches FIStatus.java)
export enum FIStatus {
  READY = 'READY',
  DENIED = 'DENIED',
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  TIMEOUT = 'TIMEOUT',
  PURGED = 'PURGED',
  DATA_NOT_RETURNED_IN_TIME = 'DATA_NOT_RETURNED_IN_TIME',
  INVALID_DATA = 'INVALID_DATA',
}

export enum RefreshDataFetchStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  REFRESH_FAILED = 'REFRESH_FAILED',
  REFRESH_CALL_FAILED = 'REFRESH_CALL_FAILED', 
}

export interface AccountDetails {
  maskedAccountNumber: string;
  linkRefNumber: string;
  fipName: string;
  fipId: string;
  fiType: FIType;
  accountType: string;
  lastRawDataFetchedAt: string;
  lastRawDataId: string;
  lastAnalyzedAt: string;
  lastRequestFIStatus: FIStatus;
  lastRequestAnalyticsStatus: string;
  referenceId: string;
  closingBalance: number;
  status: AccountStatusType;
  nextRefreshTime: string | null;
  nextManualRefreshTime: string | null;
  refreshStatus: RefreshDataFetchStatus | null;
  consentStatus: string
}

// Dashboard display asset types (UI-specific categories)
export enum DashboardAssetType {
  BANKS = 'BANKS',
  STOCKS = 'STOCKS',
  MUTUAL_FUNDS = 'MUTUAL_FUNDS',
  FD = 'FD',
  EPFO = 'EPFO',
  NPS = 'NPS',
}

export enum DepositAccountStatusUI {
  UPDATING = 'updating',
  REFRESHING = 'refreshing',
  REVOKING = 'revoking',
  SERVER_ISSUE = 'serverIssue',
}

// Analytics status enum (matches AnalyticsStatus.java)
export enum AnalyticsStatus {
  PENDING = 'PENDING',
  SYNC_PROCESSING = 'SYNC_PROCESSING',
  SYNC_FAILED = 'SYNC_FAILED',
  SYNC_COMPLETED = 'SYNC_COMPLETED',
  ASYNC_PROCESSING = 'ASYNC_PROCESSING',
  ASYNC_COMPLETED = 'ASYNC_COMPLETED',
  ASYNC_FAILED = 'ASYNC_FAILED',
  REFRESH_PROCESSING = 'REFRESH_PROCESSING',
  REFRESH_INITIATE_FAILED = 'REFRESH_INITIATE_FAILED',
}

export enum ConsentType {
  EPFO = 'EPFO',
  CREDIT = 'CREDIT',
}

// Selected account info for UI components
export interface SelectedAccountInfo {
  id: string;
  accNo: string;
  fipName: string;
  fipId: string;
  linkedAccRef: string;
}

// Month filter option
export interface MonthOption {
  value: string;
  label: string;
}

// Category code mapping by account ID
export type CategoryCodeMap = Record<string, string[]>;

// Bank account info for UI display
export interface BankAccountInfo {
  id: string;
  accNo: string;
  balance: number | string;
  lastUpdatedTime: string;
  ifscCode: string;
  fipName: string;
  accType: string;
  holderName: string;
  holderAddress: string;
  holderEmail: string;
  dailyAnalytics?: unknown;
  fipId: string;
  date: string;
  consentReferenceId: string;
  priority: number | null;
  linkedAccRef: string;
  // Extended properties for failure states
  pending?: boolean;
  dataFailed?: boolean;
  maskedAccountNumber?: string;
  status?: AccountStatusType;
}

// Fixed/Recurring Deposit info
export interface DepositInfo {
  fipId: string;
  institutionName?: string;
  totalInvestedAmount: number;
  totalPortfolioValue: number;
  interestRate?: number | string;
  maturityAmount?: number;
  dateOfMaturity?: string;
}

// Mutual Fund scheme info
export interface MutualFundScheme {
  isin: string;
  mutualFundName: string;
  fundHouse: string;
  schemeCategory: string;
  units: number;
  nav: number;
  totalPortfolioValue: number;
  totalInvestedValue: number;
  accountDetails?: Array<{
    folioNo: string;
    units: number;
    investedValue: number;
    currentValue: number;
  }>;
  folioNumbers?: string[];
  folioCount?: number;
  currentValue?: number;
}

// Stock holding analysis (matches HoldingAnalysis.java)
export interface HoldingAnalysis {
  equityId?: string;
  units?: number;
  isin?: string;
  marketCap?: string;
  date?: string;
  sector?: string;
  companyName?: string;
  bsePrices?: number;
  nsePrices?: number;
  industry?: string;
  symbol?: string;
  logo?: string;
  sectorPeRatio?: number;
  peRatio?: number;
  marketCapValue?: number;
  currentPrice?: number;
  oneWeekPrice?: number;
  oneMonthPrice?: number;
  threeMonthPrice?: number;
  oneYearPrice?: number;
  oneWeekPercentage?: number;
  oneMonthPercentage?: number;
  threeMonthPercentage?: number;
  oneYearPercentage?: number;
  lastWeekPL?: number;
}

// ETF account details
export interface ETFFundAccountDetails {
  accountId?: string;
  units?: number;
  investedValue?: number;
  currentValue?: number;
}

// ETF portfolio distribution analysis
export interface ETFPortfolioDistributionAnalysis {
  name?: string;
  value?: number;
}

// ETF Fund wise analysis (matches ETFFundWiseAnalysis.java)
export interface ETFFundWiseAnalysis {
  isin?: string;
  fundName?: string;
  logo?: string;
  benchmarkIndex?: string;
  accountDetails?: ETFFundAccountDetails[];
  totalInvestedAmount?: number;
  totalPortfolioValue?: number;
  nav?: number;
  units?: number;
  profitAmount?: number;
  absolutePercentageReturn?: number;
  expenseRatio?: number;
  cagr?: number;
  xirr?: number;
  portfolioDistributionAnalysis?: ETFPortfolioDistributionAnalysis[];
  marketCapBasedDistributionAnalysis?: Record<string, number>;
  equityOnlyMarketCapBasedDistributionAnalysis?: Record<string, number>;
  sectorBasedDistributionAnalysis?: Record<string, number>;
  historicalNav?: unknown;
  marketCapitalizationType?: string;
  fundType?: string;
  schemeCategory?: string;
  schemeSubCategory?: string;
  errorFlags?: string[];
}

// Stocks holdings response (matches StocksHoldings.java)
export interface StocksHoldingsResponse {
  holdings?: HoldingAnalysis[];
  marketCap?: Record<string, string>;
  sector?: Record<string, string>;
  etfFundWiseAnalysis?: ETFFundWiseAnalysis[];
}

// Display holding type for combined stocks and ETFs
export interface DisplayHolding {
  companyName?: string;
  fundName?: string;
  name?: string;
  bsePrices?: number;
  nav?: number;
  price?: number;
  units?: number;
  logo?: string;
  type: 'STOCK' | 'ETF';
  sector?: string;
  fundType?: string;
}

// Account discovery status for AA flow
export enum AccountDiscoveryStatus {
  Default = 'Default',
  AccountsDiscovered = 'AccountsDiscovered',
  NoAccountsFound = 'NoAccountsFound',
}

export interface PassbookEntry {
  wage_month: string;
  tr_date_my: string;
  tr_approved: string;
  particular: string;
  cr_ee_share: string;
  cr_er_share: string;
  cr_pen_bal: string;
  db_cr_flag: string;
  approved_on: string;
  dr_ee_share?: string;
  dr_er_share?: string;
  pension_share?: string;
  nic?: string;
}

export interface EstablishmentDetails {
  member_id: string;
  est_name: string;
  est_id?: string;
  office?: string;
  doj_epf: string;
  doj_eps?: string;
  doc_epf: string;
  doc_eps: string;
  closing_balance?: string;
  passbook: PassbookEntry[];
  passbook_status?: string;
}

export interface ShareTotal {
  debit: number;
  credit: number;
  balance: number;
}

export interface OverallPFBalance {
  current_pf_balance: number;
  pension_balance: number;
  employee_share_total?: ShareTotal;
  employer_share_total?: ShareTotal;
}

export interface EmployeeDetails {
  uan: string;
  member_name: string;
  dob: string;
  father_husband_name?: string;
  mobile?: string;
  email?: string;
}

export interface EPFAPIResult {
  employee_details: EmployeeDetails;
  est_details: EstablishmentDetails[];
  overall_pf_balance: OverallPFBalance;
}

export interface EPFChartDataPoint {
  year: number;
  value: number;
  actualValue: number;
}

// NPS Types
export interface NPSHolding {
  schemeId?: string;
  schemeName?: string;
  schemeType?: string;
  tierType?: string;
  nav?: number;
  units?: number;
  investedValue?: number;
  currentValue?: number;
  allocationPercentage?: number;
}

export interface NPSSummary {
  totalCurrentValue?: number;
  totalInvestedValue?: number;
  totalUnits?: number;
  pranNumber?: string;
  lastUpdated?: string;
}

export interface NPSAnalysisResult {
  summary?: NPSSummary;
  holdings?: NPSHolding[];
}

export interface RecapSpendCategory {
  rank: number;
  categoryName: string;
  amount: number;
  merchants: string[];
}

export interface RecapSpendingMonth {
  rank: number;
  month: string;
  amount: number;
}

export interface RecapSummary {
  recapStartMonth: string;
  recapEndMonth: string;
  totalSpends: number;
  top5SpendCategories: RecapSpendCategory[];
  totalIncome: number;
  salariedIncome: number;
  rentalIncome: number;
  interestIncome: number;
  totalOtherIncome: number;
  totalMerchantSettlements: number;
  totalCashDeposits: number;
  totalP2PCredits: number;
  totalSavings: number;
  totalInvestment: number;
  totalCreditAmount: number;
  totalDebitAmount: number;
  totalCreditTxns: number;
  totalDebitTxns: number;
  top3SpendingMonths: RecapSpendingMonth[];
}

export interface OverviewMonthlyCashFlow {
  year: number;
  month: number;
  totalDebitAmount: number;
  totalCreditAmount: number;
}

export interface OverviewAnalysis {
  userId: string;
  totalAssetsBalance: number;
  savingsBalance: number;
  investmentsBalance: number;
  bankBalance: number;
  mutualFundsBalance: number;
  stocksAndETFsBalance: number;
  totalDebitAmount: number;
  totalCreditAmount: number;
  fdRdBalance: number;
  overviewMonthlyCashFlow: OverviewMonthlyCashFlow[];
  recapSummary: RecapSummary;
}

export type NPSSchemeCategory = keyof typeof NPS_CATEGORY;
export type Icon = keyof typeof NPS_ICON_MAPPING;

// ======================================================
// RAW API TYPES
// ======================================================

export type RawNPSDataHolding = {
  blockedUnits?: string | null;
  freeUnits?: string | null;
  nav?: string | null;
  schemeName?: string | null;
  schemeId?: string | null;
  allocationPercentage?: string | null;
  totalUnits?: string | null;
  totalValueOfScheme?: string | null;
};

export type RawNPSDataTierHolding = {
  currentValue?: string;
  amountInTransition?: string;
  schemePreferenceType?: string;
  Tier1Holding?: RawNPSDataHolding[];
  Tier2Holding?: RawNPSDataHolding[];
};

export type RawNPSDataSummary = {
  openingDate?: string | null;
  currentValue?: string;
  status?: string;
  tier1Status?: string | null;
  tier2Status?: string | null;
  tier1NAVDate?: string | null;
  tier2NAVDate?: string | null;
  debtAssetValue?: string;
  equityAssetValue?: string;
  otherAssetValue?: string;

  Holdings?: {
    Tier1Holdings?: RawNPSDataTierHolding | null;
    Tier2Holdings?: RawNPSDataTierHolding | null;
  };
};

export type RawNPSDataHolderInfo = {
  name?: string;
  nominee?: string;
  pranId?: string;
  address?: string;
  email?: string;
  kycCompliance?: string;
};

export type RawNPSData = {
  maskedAccNumber?: string | null;
  type?: string;
  linkedAccRef?: string;
  fipName?: string;
  brokerName?: string | null;
  maskedDematId?: string | null;

  Profile?: {
    Holders?: {
      Holder?: RawNPSDataHolderInfo[] | null;
    } | null;
  } | null;

  Summary?: RawNPSDataSummary | null;
  Transactions?: null;
};

export type RawNPSDataObject = {
  dataRequestDetailsId?: string;
  rawData?: RawNPSData;
};

// ======================================================
// NPS NORMALIZED RESPONSE TYPES
// ======================================================

export type NPSDataHolding = {
  schemeName?: string;
  schemeId?: string;
  schemeCategory?: 'E' | 'C' | 'G' | 'A';
  assetClass?:
    | 'EQUITY'
    | 'CORPORATE_BONDS'
    | 'GOVERNMENT_SECURITIES'
    | 'ALTERNATIVE_ASSET';
  allocationPercentage?: number;
  totalUnits?: number;
  blockedUnits?: number;
  freeUnits?: number;
  nav?: number;
  totalValueOfScheme?: number;
};

export type NPSAllocationBreakdownItem = {
  schemeCategory?: string;
  percentage?: number;
  value?: number;
};

export type NPSAllocationBreakdown = {
  equity?: NPSAllocationBreakdownItem | null;
  corporateBonds?: NPSAllocationBreakdownItem | null;
  government?: NPSAllocationBreakdownItem | null;
  alternative?: NPSAllocationBreakdownItem | null;
};

export type NPSRiskProfile = {
  equityExposure?: number;
  debtExposure?: number;
  riskCategory?: 'AGGRESSIVE' | 'MODERATE' | 'CONSERVATIVE';
  investmentChoice?: string;
};

export type NPSTierAnalysis = {
  currentValue?: number;
  amountInTransition?: number;
  fundHouseName?: string | null;
  fundHouseCode?: string | null;
  schemePreferenceType?: string | null;
  holdings?: NPSDataHolding[];
};

export type NPSOverallAnalysis = {
  totalCurrentValue?: number;
  status?: string;
  openingDate?: string | null;
  accountAgeInMonths?: number | null;
  tier1Status?: string | null;
  tier2Status?: string | null;
  tier1NAVDate?: string | null;
  tier2NAVDate?: string | null;
  debtAssetValue?: number;
  equityAssetValue?: number;
  otherAssetValue?: number;
  tier1CurrentValue?: number;
  tier2CurrentValue?: number;
  hasTier2?: boolean;
  frozen?: boolean;
  inTransition?: boolean;
};

export type NPSHolderInfo = {
  name?: string;
  maskedPranId?: string | null;
  nominee?: string | null;
  fipId?: string | null;
  fipName?: string | null;
};

export type NPSDataObject = {
  overallAnalysis?: NPSOverallAnalysis;
  tier1Analysis?: NPSTierAnalysis;
  tier2Analysis?: NPSTierAnalysis;
  allocationBreakdown?: NPSAllocationBreakdown;
  riskProfile?: NPSRiskProfile;
  holderInfo?: NPSHolderInfo;
  linkedAccRef?: string;
};

// =============================================
// FINAL PARSED TYPES
// =============================================

export type ParsedNPSSchemeHolding = {
  schemeName: string | null;
  schemeId: string | null;
  schemeCategory: NPSSchemeCategory | null;
  assetClass:
    | 'EQUITY'
    | 'CORPORATE_BONDS'
    | 'GOVERNMENT_SECURITIES'
    | 'ALTERNATIVE_ASSET'
    | null;
  allocationPercentage: number | null;
  totalUnits: number | null;
  blockedUnits: number | null;
  freeUnits: number | null;
  nav: number | null;
  totalValueOfScheme: number | null;
};

export type ParsedSchemeAnalysis = {
  schemeName: string | null;
  schemeType: 'NPS' | null;
  schemeId: string | null;
  schemeCategory: string | null;
  assetClass:
    | 'EQUITY'
    | 'CORPORATE_BONDS'
    | 'GOVERNMENT_SECURITIES'
    | 'ALTERNATIVE_ASSET'
    | null;
  fipName: string | null;
  pranId: string | null;
  totalPortfolioValue: number | null;
  totalInvestedValue: number | null;
  allocationPercentage: number | null;
  totalUnits: number | null;
  freeUnits: number | null;
  blockedUnits: number | null;
  nav: number | null;
  tier: 'Tier1' | 'Tier2' | null;
};

export type ParsedAccountAnalysis = {
  schemeName: string | null;
  schemeType: 'NPS' | null;
  fipName: string | null;
  pranId: string | null;
  linkedAccRef: string | null;
  totalPortfolioValue: number | null;
  status: string | null;
  tier1Status: string | null;
  tier2Status: string | null;
  openingDate: string | null;
  accountAgeInMonths: number | null;
  equityAssetValue: number | null;
  debtAssetValue: number | null;
  otherAssetValue: number | null;
  tier1CurrentValue: number | null;
  tier2CurrentValue: number | null;
  hasTier2: boolean | null;
  frozen: boolean | null;
  inTransition: boolean | null;
  allocationBreakdown: NPSAllocationBreakdown | null;
  riskProfile: NPSRiskProfile | null;
};

export type ParsedNPSResponse = {
  data: {
    overallAnalysis: {
      totalPortfolioValue: number;
      totalAccounts: number;
      activeAccounts: number;
      frozenAccounts: number;
      totalTier1Value: number;
      totalTier2Value: number;
    };
    schemeWiseAnalysis: ParsedSchemeAnalysis[];
    accountWiseAnalysis: ParsedAccountAnalysis[];
    tier1HoldingsData: ParsedNPSSchemeHolding[];
    tier2HoldingsData: ParsedNPSSchemeHolding[];
  };
};


export type analyticsStatusDataRefType = {
  savings: number;
  mutualFunds: number;
  equities: number;
  etf: number;
  fdRd: number;
  epfo: boolean;
  epfoAmount: number;
  nps: number;
  creditBureau: boolean;
  prev: {
    savings: number;
    mutualFunds: number;
    equities: number;
    etf: number;
    fdRd: number;
    epfo: boolean;
    nps: number;
    creditBureau: boolean;
  };
};